import { useTranslation } from 'react-i18next'

import styles from './StepVisualShortlist.module.scss'

/**
 * Шаг 04 — «Получаешь выборку с описанием сигналов».
 *
 * Миниатюра карточки лида из `containers/AppWizard/launch/LeadCard.tsx`, ужатая в 320×180.
 *
 * РАСХОДИТСЯ С ПРОДУКТОМ в трёх местах, и это долг, а не решение: карточка с тех пор увела полосу
 * находок в правый столбец под балл, дала каждому факту вендорской линии свой значок вместо одного
 * на всю линию и завела поле адреса со звёздочками до оплаты. Миниатюра всё ещё рисует прежнее.
 * Пока это лендинг, а не продукт, расхождение стоит дёшево — но названо здесь, чтобы не читаться
 * как «так карточка и выглядит».
 *
 * Зоны и порядок — те же, что были в продукте на момент отрисовки:
 *
 *   кто это      имя-ссылка · должность verbatim, справа возраст сигнала зелёным
 *   компания     строка вендора со своей иконкой, чтобы не читалась как слова человека
 *   сигналы      лиловая полоса чипов с меткой SIGNALS и значком «есть цитата»
 *   почему       зелёный блок WHY IT MATCHES + WHAT TO DO + золотая платная дверь
 *   балл         правая колонка за линейкой: qualified, 86/100 и три оси
 *
 * Каждая ПОДПИСЬ здесь — настоящая строка продукта: `Signals`, `Why it matches`, `What to do`,
 * `fit / intent / decision power`, `qualified`, `Generate first message`, `Open in Intch`, и
 * «signal 2d ago» — дословно то, что печатают `app.lead.*` в en.json. Форма карточки снята с
 * реального прогона (launch.png).
 *
 * ЧЕЛОВЕК ЖЕ ВЫДУМАН, и это не небрежность. Карточка на скриншоте принадлежит настоящему участнику
 * Intch: имя, работодатель и найденный сигнал — то, что он опубликовал о себе, а не материал для
 * маркетинга, на который он не подписывался. Живые плиты лендинга по той же причине показывают
 * только инициалы (`components/plates/PlateScore.tsx`), а соседние шаги — придуманных
 * «Maria Kessler» и «Reagan M.». Настоящими остаются подписи продукта, вымышленными — имя и
 * компания.
 *
 * Цвета — те же токены, что в LeadCard.module.scss: --say для полосы сигналов (это claim
 * ЧЕЛОВЕКА), --ok для блока «почему» (это НАШ вывод), --gold для платной двери. Смешать их
 * здесь значило бы показать на лендинге экран, которого в продукте нет.
 */
export const StepVisualShortlist = () => {
  const { t } = useTranslation()

  return (
    <div className={styles.root}>
      <div className={styles.main}>
        {/* ---- кто это ---- */}
        <div className={styles.head}>
          <span className={styles.rank}>2</span>
          <span className={styles.name}>
            Elena Ruiz
            <i className={styles.out}>↗</i>
          </span>
          <span className={styles.role}>CEO &amp; Founder</span>
          <span className={styles.age}>{t('landing.how.cards.signalAge', { age: '2d' })}</span>
        </div>

        {/* Строка вендора: иконка вместо буквы — в продукте она отделяет факт о компании от того,
            что человек написал сам. */}
        <div className={styles.facts}>
          <i className={styles.factsIcon} aria-hidden="true" />
          Northlake Care · Wellness and Fitness · 11–50
        </div>

        {/* ---- сигналы ---- */}
        <div className={styles.signals}>
          <b className={styles.signalsLabel}>{t('app.lead.signalsLabel')}</b>
          <div className={styles.signalRow}>
            <span className={`${styles.chip} ${styles.kindAsk}`}>
              part-time role
              <i className={styles.info} aria-hidden="true" />
            </span>
            {/* Второй чип серый, а не синий: в продукте «team leadership» — это kind-capability, что
                человек УМЕЕТ, а не о чём попросил. Цвет здесь несёт вид сигнала, и покрасить
                компетенцию как событие значило бы показать состояние, которого в карточке нет. */}
            <span className={`${styles.chip} ${styles.kindCapability}`}>
              team leadership
              <i className={styles.info} aria-hidden="true" />
            </span>
          </div>
        </div>

        {/* ---- почему совпало ---- */}
        <div className={styles.why}>
          <b className={styles.whyLabel}>{t('app.lead.whyMatch')}</b>
          <span className={styles.whyText}>
            This company is a buyer for Intch, and its CEO is seeking a remote part-time
            organizational leader.
          </span>
          <span className={styles.move}>
            <b className={styles.moveLabel}>{t('app.lead.whatToDo')}</b>
            <span className={styles.moveText}>Write to her as the decision-maker.</span>
          </span>
          <span className={styles.sequence}>{t('app.lead.plan')}</span>
        </div>
      </div>

      {/* ---- балл: отдельная колонка за линейкой, как в продукте ---- */}
      <aside className={styles.side}>
        <span className={styles.verdict}>{t('app.lead.verdicts.qualified')}</span>

        <div className={styles.overall}>
          <span className={styles.overallValue}>
            <b>86</b>
            <small>/100</small>
          </span>
          <span className={styles.overallTrack}>
            <i className={styles.fillGood} style={{ width: '86%' }} />
          </span>
        </div>

        <div className={styles.axis}>
          <span className={styles.axisLabel}>{t('app.lead.axes.icp')}</span>
          <span className={styles.axisTrack}>
            <i className={styles.fillGood} style={{ width: '80%' }} />
          </span>
          <b className={styles.textGood}>8</b>
        </div>
        <div className={styles.axis}>
          <span className={styles.axisLabel}>{t('app.lead.axes.intent')}</span>
          <span className={styles.axisTrack}>
            <i className={styles.fillGood} style={{ width: '80%' }} />
          </span>
          <b className={styles.textGood}>8</b>
        </div>
        <div className={styles.axis}>
          <span className={styles.axisLabel}>{t('app.lead.axes.dm')}</span>
          <span className={styles.axisTrack}>
            <i className={styles.fillGold} style={{ width: '100%' }} />
          </span>
          <b className={styles.textGold}>10</b>
        </div>

        {/* Прижаты к низу линейки, как в продукте: действие предлагается ПОСЛЕ причины. */}
        <div className={styles.actions}>
          <span className={styles.chat}>{t('app.lead.chat')}</span>
          <span className={styles.openIn}>{t('app.lead.openIn')} ↗</span>
        </div>
      </aside>
    </div>
  )
}
